import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RoutePath } from 'router/router.config';
import { RootState } from 'store/store';
import {
  contactDataFormDefaultValues,
  ContactDataFormType,
} from 'pages/ContactDataStep/ContactDataStep.config';
import {
  personalDataFormDefaultValues,
  PersonalDataFormType,
} from 'pages/PersonalDataStep/PersonalDataStep.config';
import {
  securityDataFormDefaultValues,
  SecurityDataFormType,
} from 'pages/SecurityDataStep/SecurityDataStep.config';

export interface SetStepFormValues<T extends string, T2 extends object> {
  type: T;
  formValues: T2;
}

export type RegistrationAction =
  | SetStepFormValues<'SET_CONTACT_DATA_VALUES', ContactDataFormType>
  | SetStepFormValues<'SET_PERSONAL_DATA_VALUES', PersonalDataFormType>
  | SetStepFormValues<'SET_SECURITY_DATA_VALUES', SecurityDataFormType>
  | SetStepFormValues<
      'SET_PDV_CODE',
      {
        accountCode: number;
        oldPin: number;
      }
    >;

const initialState = {
  contactDataStep: {
    isDirty: false,
    formValues: contactDataFormDefaultValues,
    nextStep: `/${RoutePath.PersonalData}`,
  },
  personalDataStep: {
    isDirty: false,
    formValues: personalDataFormDefaultValues,
    prevStep: `/${RoutePath.Root}`,
    nextStep: `/${RoutePath.SecurityData}`,
  },
  securityDataStep: {
    formValues: securityDataFormDefaultValues,
    prevStep: `/${RoutePath.PersonalData}`,
  },
  pdvCode: {
    fromPdv: false,
    accountCode: '',
    oldPin: '',
  },
};

export type RegistrationState = typeof initialState;

const RegistrationValuesSlice = createSlice({
  name: 'RegistrationValues',
  initialState,
  reducers: {
    setValues: (state, action: PayloadAction<RegistrationAction>) => {
      const { type, formValues } = action.payload;
      switch (type) {
        case 'SET_CONTACT_DATA_VALUES': {
          state = {
            ...state,
            contactDataStep: {
              ...state.contactDataStep,
              isDirty: true,
              formValues,
            },
          };
          break;
        }
        case 'SET_PERSONAL_DATA_VALUES': {
          state = {
            ...state,
            personalDataStep: {
              ...state.personalDataStep,
              isDirty: true,
              formValues,
            },
          };
          break;
        }
        case 'SET_SECURITY_DATA_VALUES': {
          state = {
            ...state,
            securityDataStep: {
              ...state.securityDataStep,
              formValues,
            },
          };
          break;
        }
        case 'SET_PDV_CODE': {
          state = {
            ...state,
            pdvCode: {
              ...state.pdvCode,
              fromPdv: true,
            },
          };
          break;
        }
      }
      return state;
    },
    setRegistrationState: (_, action: PayloadAction<RegistrationState>) => {
      return action.payload;
    },
  },
});

export const { setValues, setRegistrationState } =
  RegistrationValuesSlice.actions;

export default RegistrationValuesSlice.reducer;

export const selectRegistrationStep =
  <T extends keyof typeof initialState>(step: T) =>
  (state: RootState): (typeof initialState)[T] =>
    state.registration[step];

export const selectRegistrationState = (state: RootState) => state.registration;
